/**
 * Generate Report button — drops onto any page that's a natural source
 * for one report kind (CrawlDetail → crawl, PropertyGSC → traffic,
 * PropertyBacklinks → backlinks). Spawns the background subprocess via
 * the API + polls /api/reports?kind=...&[crawl|property]_id=... until
 * the new row appears, then surfaces the download link.
 *
 * Two modes:
 *   - <GenerateReportButton crawlId={N} />          → kind=crawl
 *   - <GenerateReportButton propertyId={N} kind=    → kind=traffic
 *                              "traffic|backlinks" />  or backlinks
 *
 * The kind is implicit in which page hosts the button — Stefan wanted
 * "kind picked by where you click, not by a dropdown."
 */
import { useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import {
  type Report,
  reportDownloadUrl,
  useGenerateCrawlReport,
  useGeneratePropertyReport,
  useReports,
} from "@/lib/queries";

type Props =
  | { crawlId: number; propertyId?: never; kind?: never }
  | { propertyId: number; kind: "traffic" | "backlinks"; crawlId?: never };

export default function GenerateReportButton(props: Props) {
  const isCrawl = "crawlId" in props && props.crawlId !== undefined;
  const isProperty = "propertyId" in props && props.propertyId !== undefined;

  // Track the moment the user clicked so we know which row in the
  // polling list is "ours" (rather than just the latest existing row
  // for the same source).
  const [requestedAt, setRequestedAt] = useState<number | null>(null);

  const crawlMut = useGenerateCrawlReport();
  const propMut = useGeneratePropertyReport();

  // Poll every 8s while a generation is in flight. Drops back to 60s
  // (default in useReports) once the new row arrives.
  const inFlight = requestedAt !== null;
  const list = useReports({
    kind: isCrawl ? "crawl" : (props as { kind: "traffic" | "backlinks" }).kind,
    crawl_id: isCrawl ? (props as { crawlId: number }).crawlId : null,
    property_id: isProperty
      ? (props as { propertyId: number }).propertyId
      : null,
    limit: 5,
    refetchMs: inFlight ? 8_000 : 60_000,
  });

  // The "fresh" report = the newest one created AFTER we clicked.
  const fresh: Report | undefined =
    inFlight && list.data?.items
      ? list.data.items.find(
          (r) => Date.parse(r.created_at) >= requestedAt,
        )
      : undefined;

  const onClick = async () => {
    setRequestedAt(Date.now());
    try {
      if (isCrawl) {
        await crawlMut.mutateAsync({ crawl_id: (props as { crawlId: number }).crawlId });
      } else {
        await propMut.mutateAsync({
          property_id: (props as { propertyId: number }).propertyId,
          kind: (props as { kind: "traffic" | "backlinks" }).kind,
        });
      }
    } catch (e) {
      console.error("Generate report failed:", e);
      setRequestedAt(null); // Reset so the user can retry.
    }
  };

  const labelKind = isCrawl
    ? "Crawl Report"
    : (props as { kind: string }).kind === "traffic"
      ? "Traffic Report"
      : "Backlinks Report";

  // ── States ──
  // 1. Idle (never clicked) → enabled button
  // 2. Posting → button disabled with spinner ("Starting…")
  // 3. In flight (poll loop) → spinner ("Generating…")
  // 4. Done (fresh row found) → download link
  // 5. Errored → red explanation + retry

  if (fresh) {
    return (
      <a
        href={reportDownloadUrl(fresh.id)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
      >
        <Download className="h-3.5 w-3.5" /> {labelKind} ready — download
      </a>
    );
  }

  const mut = isCrawl ? crawlMut : propMut;
  const isPosting = mut.isPending;

  if (inFlight || isPosting) {
    return (
      <button
        type="button"
        disabled
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground"
        title={
          isPosting
            ? "Spawning the report generator…"
            : "Reportify pass typically takes 1-3 minutes."
        }
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {isPosting ? "Starting…" : `Generating ${labelKind}…`}
      </button>
    );
  }

  if (mut.isError) {
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onClick}
          className="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-500/20 dark:text-red-300"
        >
          <FileText className="h-3.5 w-3.5" /> Retry — {labelKind}
        </button>
        <span className="text-xs text-red-600 dark:text-red-400">
          {(mut.error as Error)?.message ?? "failed"}
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
      title={`Generate a customer-facing PDF ${labelKind} from the data on this page`}
    >
      <FileText className="h-3.5 w-3.5" /> Generate {labelKind}
    </button>
  );
}
